import React from "react";
import { Box, Button, Typography, Grid } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import clsx from "clsx";
import moment from "moment";
import { Tooltip } from "../Tooltip";
import TodoItemImage from "../TodoList/TodoItemImage";
import { PropTypes } from "prop-types";
import { courseTypes, todoTypes } from "../../constants";
import CourseActionButton from "../Buttons/CourseActionButton";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(theme => ({
  overdue: {
    color: theme.palette.error.main
  },
  new: {
    color: theme.palette.visto.main
  },
  dueDate: {
    color: theme.palette.secondary[300]
  },
  status: {
    fontStyle: "italic",
    flex: 1,
    textAlign: "center",
    marginRight: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      textAlign: "right"
    }
  },
  button: {
    minWidth: 85
  },
  buttonContainer: {
    marginLeft: "auto",
    [theme.breakpoints.down("sm")]: {
      marginLeft: "0"
    }
  },
  statusContainer: {
    alignSelf: "center"
  }
}));

const AssignmentStatus = props => {
  const classes = useStyles();
  let status = "";
  if (props.new || props.type === todoTypes.message) {
    status = "New";
  }
  if (props.overdue) {
    status = "Overdue";
  } else if (props.dueDate && props.type == "Assignment") {
    status = "dueDate";
  }
  return status ? (
    <Typography
      variant="body2"
      className={clsx(classes.status, {
        [classes.overdue]: props.overdue,
        [classes.dueDate]: !props.overdue && props.dueDate,
        [classes.new]: props.new || props.type === todoTypes.message
      })}
    >
      {props.dueDate && !props.overdue
        ? `${moment(props.dueDate).format("MM/DD/YYYY")}`
        : status}
    </Typography>
  ) : null;
};

const TodoListItemButton = ({ userAssignment, onClick, todoType }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  if (
    todoType === todoTypes.assignment ||
    todoType === todoTypes.myCourses ||
    todoType === todoTypes.policy
  ) {
    return (
      <CourseActionButton
        courseType={userAssignment.courseType}
        isEnrolled={!!userAssignment.enrolledTrainingObjectId}
        isStarted={
          userAssignment &&
          (userAssignment.status == "In Progress" ||
            userAssignment.status == "Pending" ||
            userAssignment.status == "Complete" ||
            (userAssignment.status == "Failed" &&
              userAssignment.courseType == courseTypes.ilt))
        }
        prereqs={userAssignment.prerequisites || []}
        onEnrollClick={onClick}
        onResumeCourseClick={onClick}
        className={classes.button}
        startDate={userAssignment.enrolledTrainingObject?.zonedStartDate}
        endDate={userAssignment.enrolledTrainingObject?.zonedEndDate}
        iltClassType={userAssignment.enrolledTrainingObject?.typeName}
      />
    );
  } else {
    return (
      <Button
        color="primary"
        variant="contained"
        className={classes.button}
        onClick={onClick}
      >
        {t("welcomeSlideOut.welcomeSlideOutButtons.read")}
      </Button>
    );
  }
};

const SlideOutTodoListItem = ({
  userAssignment,
  todoType,
  title,
  number,
  id,
  handleOpenItem,
  showAwardIncentive
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const todoListItemInfo = (
    <Box
      display="flex"
      flexDirection="column"
      pl={{ xs: 3, sm: 2 }}
      pt={{ xs: 0, sm: 1 }}
      pr={2}
    >
      <Typography variant="caption" color="textSecondary">
        {todoType == todoTypes.policy || todoType == todoTypes.myCourses
          ? t("welcomeSlideOut.todoListType.myItems")
          : todoType}
      </Typography>
      <Typography variant="body2" color="textPrimary">
        {number ? title + " | " + number : title}
      </Typography>
    </Box>
  );

  return (
    <Grid container>
      <Grid item xs={12} sm={7}>
        <Box display="flex" minHeight={72}>
          <TodoItemImage
            imageSrc={userAssignment?.courseImage}
            showAwardIncentive={showAwardIncentive}
            todoType={todoType}
          />
          {userAssignment?.content ? (
            <Tooltip
              title={
                <Typography variant="body2" color="textPrimary">
                  {userAssignment.content}
                </Typography>
              }
            >
              {todoListItemInfo}
            </Tooltip>
          ) : (
            todoListItemInfo
          )}
        </Box>
      </Grid>
      <Grid item xs={12} sm={5} className={classes.statusContainer}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexDirection={{ xs: "row-reverse", sm: "row" }}
          mt={0.75}
          mb={{ xs: 1, sm: 0 }}
          ml={{ xs: 12.25, sm: 0 }}
        >
          <AssignmentStatus
            new={userAssignment?.new}
            overdue={userAssignment?.overdue}
            type={todoType}
            dueDate={userAssignment?.dueDate}
          />
          <div className={classes.buttonContainer}>
            <TodoListItemButton
              userAssignment={userAssignment}
              todoType={todoType}
              onClick={() => handleOpenItem(todoType, id, userAssignment)}
            />
          </div>
        </Box>
      </Grid>
    </Grid>
  );
};

SlideOutTodoListItem.propTypes = {
  showAwardIncentive: PropTypes.bool,
  handleOpenItem: PropTypes.func,
  id: PropTypes.any,
  title: PropTypes.string,
  type: PropTypes.string,
  item: PropTypes.shape({
    prerequisites: PropTypes.array,
    assignmentStarted: PropTypes.bool,
    imageSrc: PropTypes.string,
    description: PropTypes.string,
    overdue: PropTypes.bool,
    new: PropTypes.bool
  })
};

export default SlideOutTodoListItem;
